import { action, computed, thunk, effectOn } from 'easy-peasy';
import type { Editor } from 'grapesjs';

import { getUrlParam } from '@utils/URLParamUtil';
import { LoadStatus } from '@interfaces/LoadProps';
import GuestProps from '@interfaces/guest/GuestProps';
import { setCompanyFormats } from '@utils/DayjsUtils';
import { hideEmptyDocumentSectionsBySectionId } from '@utils/GeneralUtils';
import ProfileVO, { ProfileBuilder, ProfileType } from '@interfaces/profiles/ProfileVO';
import { defaultProfilesSettings, isProfileSettingEnabled } from '@interfaces/profiles/ProfilesSettingsVO';
import { injectUserScript } from '@service/UserLoadService';
import { ProfileNdaVO } from '@interfaces/profiles/ProfileNdaVO';
import { triggerUsageTracker, usageTimeTrackVO } from '@utils/profile/UsageTracker';
import { ERROR_DETAILS_MAP, PROFILE_ERROR } from '@constants/ErrorConstants';
import { getAnonymousViewerId, setAnonymousViewerId } from '@utils/profile/ProfileAccessManagementUtils';
import { AccessibilityVO, ProfileAccessType } from '@interfaces/profiles/AccessibilityVO';
import ProfileRequestAccessVO, { GuestDetailsVO, RequestedUserStatus, defaultwebFormDetails } from '@interfaces/profiles/ProfileRequestAccessVO';
import ProfileContentVO, { ContentGatingStatus, ContentSourceStatus, ProfileContentType } from '@interfaces/contents/ProfileContentVO';
import {
  downloadContents,
  getCustomFonts,
  getInitialData,
  getNdaSignedStatus,
  getNdaMergeTags,
  getCustomFields,
  getProfile,
  getProfilePreference,
  getPublicProfile,
  handleProfileDownload,
  logUserActivity,
  reuploadContent,
  trackAnalyticsForBulkDownload,
  getSessions,
  getFAQs,
  getSubProcessors,
  mergeGuestFAQPage
} from '@service/ProfileService';
import { getCustomBuilderFaqSectionIds, getFaqLoadMoreCursor, getFaqsForSectionLoadMore, resolveFaqFetchSectionIds } from '@utils/faq/FAQSectionUtils';

let lastGuestFaqFetchKey = '';
import { getSectionVOMap, updateAccessVerifiedStatus, WATERMARK_PREFERENCES, getAccessibilityVO, isLandingPageQuickLink } from '@utils/profile/ProfileUtils';
import { sortWebFormFields } from '@utils/profile/GuestFormFieldUtils';
import { changedColorsMap, getColorValue } from '@utils/ThemeUtils';
import { reclaimProfileAccessRequest } from '@service/ProfileAccessManagementService';
import { downloadBatchInitiator } from '@utils/contents/DownloadHelper';
import { getContentLink } from '@utils/search/PreviewURLUtils';
import { getFileDownloadURL, type FileDownloadInput, type WatermarkSourceUpdate } from '@utils/contents/FileUtils';
import cloneDeep from 'clone-deep';
import ProfileFAQVO, { ProfileFAQResponse, UpdateSubprocessorCategoryPayload } from '@interfaces/faq/ProfileFAQVO';
import SharedViewDetails from '@interfaces/guest/SharedViewDetails';
import SectionInfoVO from '@interfaces/sections/SectionInfoVO';
import IGeneralStore from '../types/IGeneralStore';
import type GlobalStore from '../types/IGlobalStore';
import type { Palette } from '../types/IThemeStore';

type DownloadContentsCallbackResponse = {
  batchId?: string;
};

type DownloadBatchDocDetails = {
  contentName?: string;
  type?: string;
  streamId?: string;
  isWatermarked?: boolean;
};

type DownloadBatchDetailsCallbackVO = {
  data?: {
    downloadAuthKey?: string;
    response?: {
      docDetails?: DownloadBatchDocDetails;
    };
  };
};

/** Grapes editor instance extended with FAQ dataset store (not on stock grapesjs typings). */
type FAQDesignerEditor = Pick<Editor, 'runCommand'> & {
  config?: {
    storeManager?: {
      updateFAQDetails?: (payload: { faqVoMap: { [key: string]: ProfileFAQVO } }) => void;
      faqVoMap?: { [key: string]: ProfileFAQVO };
      setFAQResponseVO?: (payload: { faqResponseVO: ProfileFAQResponse }) => void;
      updateSubprocessorCategory?: (details: UpdateSubprocessorCategoryPayload) => void;
      faqResponseVO?: ProfileFAQResponse;
    };
  };
};

// Helper functions for preference data logic
const hasSharedViewIds = (requestAccess: ProfileRequestAccessVO): boolean => {
  return !!requestAccess?.preferences?.accessibility_details?.sharedViewIds?.length;
};

const hasPreferenceDataValue = (requestAccess: ProfileRequestAccessVO): boolean => {
  return hasSharedViewIds(requestAccess) || !!requestAccess?.preferences;
};

const isCustomPublicProfile = (builderType: ProfileBuilder, profileType: ProfileType): boolean => {
  return builderType === ProfileBuilder.CUSTOM && profileType === ProfileType.PUBLIC;
};

const SUBSCRIBED_STATUS = 'SUBSCRIBED';

const isGuestAlreadySubscribed = (guestProps: GuestProps, requestAccess: ProfileRequestAccessVO): boolean => {
  const guestSubscribed = guestProps.guestDetails?.subscribedDetails?.status === SUBSCRIBED_STATUS;
  const requestSubscribed =
    guestProps.isPublicUser && (requestAccess.status !== RequestedUserStatus.APPROVED || guestProps.isOtpVerified) && requestAccess.subscribedDetails?.status === SUBSCRIBED_STATUS;
  return guestSubscribed || requestSubscribed;
};

const getSharedViewDocumentContentIds = (sharedView: SharedViewDetails, contentIdBySectionId: { [sId: string]: string[] }, contentVOMap: { [cId: string]: ProfileContentVO }): Set<string> => {
  const contentIds = new Set<string>();
  const isDocumentType = (type: string) => type === ProfileContentType.DOCUMENT || type === ProfileContentType.PROJECT;

  (sharedView.sectionArtifacts || []).forEach(sectionArtifact => {
    if (sectionArtifact?.selectAll && sectionArtifact.sectionType?.toUpperCase() === ProfileContentType.DOCUMENT) {
      (contentIdBySectionId[sectionArtifact.sectionId] || []).forEach(id => {
        if (isDocumentType(contentVOMap[id]?.contentType)) contentIds.add(id);
      });
    } else {
      (sectionArtifact?.contents || []).forEach(contentRef => {
        if (isDocumentType(contentRef?.contentType?.toUpperCase()) && contentRef?.contentId) {
          contentIds.add(contentRef.contentId);
        }
      });
    }
  });

  return contentIds;
};

type SharedViewSectionHideContext = Pick<IGeneralStore, 'landingPageViewId' | 'guestProps' | 'sharedViewId' | 'sharedViewList'>;

const isDocumentSection = (sectionVO: Pick<SectionInfoVO, 'contents'> | null | undefined): boolean => {
  const contents = sectionVO?.contents;
  if (!contents?.length) return false;
  return contents.every((contentVO: ProfileContentVO) => {
    const type = contentVO?.contentType;
    return type === ProfileContentType.DOCUMENT || type === ProfileContentType.PROJECT;
  });
};

const getEmptyDocumentSectionIds = (originalSections: SectionInfoVO[], derived: ReturnType<typeof getSectionVOMap>): string[] => {
  return (originalSections || [])
    .filter(isDocumentSection)
    .map(section => section.id)
    .filter(sectionId => !derived.contentIdBySectionIdMap?.[sectionId]?.length);
};

const isSharedViewRestrictionContext = (state: SharedViewSectionHideContext, profileVO: ProfileVO): boolean => {
  return isCustomPublicProfile(profileVO.builderType, profileVO.type) && !state.landingPageViewId && !!state.guestProps?.isAccessVerified && !!state.sharedViewId && !!state.sharedViewList?.length;
};

const applyHideEmptySections = (sectionIdsToHide: string[]) => {
  hideEmptyDocumentSectionsBySectionId(sectionIdsToHide);
};

const getCustomTypographyPalette = (fileName: string): Palette['typography'] => {
  const baseName = fileName.split('.').slice(0, -1).join('.');
  const name = /^[\w-]+$/.test(baseName) ? baseName : baseName.replace(/[^a-zA-Z0-9_-]/g, '');
  const fontFamily = `${name}, 'Inter', 'Arial', sans-serif`;

  return {
    fontFamily,
    allVariants: {
      fontFamily
    },
    h1: { fontFamily },
    h2: { fontFamily },
    h3: { fontFamily },
    h4: { fontFamily },
    h5: { fontFamily },
    h6: { fontFamily },
    body1: { fontFamily },
    body2: { fontFamily },
    caption: { fontFamily }
  };
};

const applySharedViewRestrictionsAndGetHiddenEmptySections = (
  state: SharedViewSectionHideContext,
  profileVO: ProfileVO
): { derived: ReturnType<typeof getSectionVOMap>; emptyDocSectionIdsToHide: string[] } => {
  const originalSections: SectionInfoVO[] = profileVO.sections || [];
  const derived = getSectionVOMap(originalSections);

  if (!isSharedViewRestrictionContext(state, profileVO)) {
    return { derived, emptyDocSectionIdsToHide: [] };
  }

  const sharedView = state.sharedViewList?.find(view => view?.id === state.sharedViewId);
  if (sharedView?.isUserHaveAccess !== false) {
    return { derived, emptyDocSectionIdsToHide: [] };
  }

  const allRestrictedContentIds = getSharedViewDocumentContentIds(sharedView, derived.contentIdBySectionIdMap, derived.contentVOMap);
  if (!allRestrictedContentIds.size) {
    return { derived, emptyDocSectionIdsToHide: [] };
  }

  const restrictedContentIds = new Set<string>();
  const profileLevelGated = Boolean(profileVO?.ndaAccess || profileVO?.requestAccess);
  allRestrictedContentIds.forEach(contentId => {
    const contentVO = derived.contentVOMap[contentId];
    if (profileLevelGated || contentVO?.ndaAccess || contentVO?.requestAccess) {
      restrictedContentIds.add(contentId);
    }
  });

  if (!restrictedContentIds.size) {
    return { derived, emptyDocSectionIdsToHide: [] };
  }

  const filteredSections: SectionInfoVO[] = originalSections.map(sectionVO => ({
    ...sectionVO,
    contents: sectionVO.contents?.filter(contentVO => !restrictedContentIds.has(contentVO.id)) || []
  }));

  profileVO.sections = filteredSections;
  const nextDerived = getSectionVOMap(filteredSections);
  const emptyDocSectionIdsToHide = getEmptyDocumentSectionIds(originalSections, nextDerived);

  return { derived: nextDerived, emptyDocSectionIdsToHide };
};

/** Init API includes profilesSettingsVO on profileVO; public GET APIs do not — preserve for guest document sort. */
const withPreservedProfilesSettingsVO = (next: ProfileVO, current?: ProfileVO): ProfileVO => {
  if (next.profilesSettingsVO || !current?.profilesSettingsVO) {
    return next;
  }
  return { ...next, profilesSettingsVO: current.profilesSettingsVO };
};

const generalStore: IGeneralStore = {
  guestProps: {} as GuestProps,
  errorKey: getUrlParam('error'),
  featurePreference: { profile_agent: false, profile_agent_v2: false },
  profilesSettingsVO: defaultProfilesSettings,
  profileLoaded: LoadStatus.LOADING,
  basicProfileVO: {} as ProfileVO,
  profileVO: {} as ProfileVO,
  needDocumentRerender: false,
  watermarkVO: {},
  ndaContentVO: {} as ProfileNdaVO,
  ndaMergeTagKeyList: [],
  webFormFields: [],
  webFormDetails: defaultwebFormDetails,
  sectionVOMap: {},
  contentVOMap: {},
  contentIdBySectionId: {},
  sectionIds: [],
  contentIds: [],
  showAskFeedbackPopper: false,
  closedAskInfo: false,
  closedAskFeedbackPopper: false,
  validateNdaSignStatus: false,
  sessionDetails: [],
  reloadInitialData: false,
  setGuestProps: action((state, payload) => {
    state.guestProps = { ...state.guestProps, ...payload };
    updateAccessVerifiedStatus(state.guestProps.isAccessVerified);
  }),
  updateGuestDetails: action((state, payload) => {
    state.guestProps.guestDetails = { ...state.guestProps.guestDetails, ...payload };
  }),
  setErrorKey: action((state, payload) => {
    state.errorKey = payload;
  }),
  setProfileLoaded: action((state, payload) => {
    state.profileLoaded = payload;
  }),
  setBasicProfileVO: action((state, payload) => {
    state.basicProfileVO = withPreservedProfilesSettingsVO(payload, state.basicProfileVO);
  }),
  setProfileVO: action((state, payload) => {
    const profileVO: ProfileVO = cloneDeep(withPreservedProfilesSettingsVO(payload, state.basicProfileVO));
    profileVO.isLocked = profileVO.requestAccess || profileVO.ndaAccess;

    const { derived, emptyDocSectionIdsToHide } = applySharedViewRestrictionsAndGetHiddenEmptySections(state, profileVO);

    applyHideEmptySections(emptyDocSectionIdsToHide);

    state.profileVO = profileVO;
    state.sectionVOMap = derived.sectionVOMap;
    state.contentVOMap = derived.contentVOMap;
    state.contentIdBySectionId = derived.contentIdBySectionIdMap;
    state.sectionIds = Object.keys(state.contentIdBySectionId);
    state.contentIds = derived.contentIds;

    // Sticky across Shared View switches so Verify Access can show on unlocked/empty views
    // when other views still have access-controlled files.
    if (profileVO.requestAccess || Boolean(profileVO.additionalMap?.isProfileGated) || Object.values(derived.contentVOMap).some(item => item.requestAccess)) {
      state.hasAccessControlledContent = true;
    }
  }),
  setDocumentRerender: action((state, payload) => {
    state.needDocumentRerender = payload;
  }),
  setFeaturePreference: action((state, payload) => {
    state.featurePreference = { ...state.featurePreference, ...payload };
  }),
  setProfilesSettingsVO: action((state, payload) => {
    state.profilesSettingsVO = payload;
  }),
  injectScript: thunk((_actions, _payload, { getState }) => {
    const { profilesSettingsVO, guestProps, basicProfileVO } = getState();
    if (!isProfileSettingEnabled(profilesSettingsVO, 'PROFILE_WIDGETS')) {
      return;
    }

    const profileId = guestProps.profileId || basicProfileVO.id || 'profile';
    (profilesSettingsVO.widgetList ?? []).forEach((widget, index) => {
      const scriptSource = widget?.trim();
      if (!scriptSource) {
        return;
      }
      injectUserScript(`profile-widget-${profileId}-${index}`, scriptSource);
    });
  }),
  setSessionDetails: action((state, payload) => {
    state.sessionDetails = payload;
  }),
  setReloadInitialData: action((state, payload) => {
    state.reloadInitialData = payload;
  }),
  setNdaContentVO: action((state, payload) => {
    state.ndaContentVO = payload;
  }),
  setNdaMergeTagKeyList: action((state, payload) => {
    state.ndaMergeTagKeyList = payload;
  }),
  setWebFormFields: action((state, payload) => {
    state.webFormFields = payload;
  }),
  setWatermarkVO: action((state, payload) => {
    state.watermarkVO = payload;
  }),
  setSectionVOMap: action((state, payload) => {
    state.sectionVOMap = payload;
  }),
  setContentVOMap: action((state, payload) => {
    state.contentVOMap = payload;
  }),
  setContentIdBySectionId: action((state, payload) => {
    state.contentIdBySectionId = payload;
  }),
  setContentIds: action((state, payload) => {
    state.contentIds = payload;
  }),
  setShowAskFeedbackPopper: action((state, payload) => {
    state.showAskFeedbackPopper = payload;
  }),
  setClosedAskInfo: action((state, payload) => {
    state.closedAskInfo = payload;
  }),
  setClosedFeedbackPopper: action((state, payload) => {
    state.closedAskFeedbackPopper = payload;
  }),
  setValidateNdaSignStatus: action((state, payload) => {
    state.validateNdaSignStatus = payload;
  }),

  isDesignerView: false,
  setIsDesignerView: action((state, payload) => {
    state.isDesignerView = payload;
  }),

  highlightedElement: '',
  setHighlightedElement: action((state, payload) => {
    state.highlightedElement = payload;
  }),

  reloadProfile: true,
  setReloadProfile: action((state, payload) => {
    state.reloadProfile = payload;
  }),

  customBuilderFaqSections: null,
  setCustomBuilderFaqSections: action((state, payload) => {
    state.customBuilderFaqSections = payload;
  }),

  sharedViewList: [],
  setSharedViewList: action((state, payload) => {
    state.sharedViewList = payload;
  }),

  sharedViewId: '',
  setSharedViewId: action((state, payload) => {
    state.sharedViewId = payload;
  }),

  landingPageViewId: '',
  setLandingPageViewId: action((state, payload) => {
    state.landingPageViewId = payload;
  }),

  previousFAQSharedViewId: '',
  setPreviousFAQSharedViewId: action((state, payload) => {
    state.previousFAQSharedViewId = payload;
  }),

  fetchInitialData: thunk(async (actions, { onLoaded }, { getStoreState, getStoreActions }) => {
    const state = getStoreState() as GlobalStore;
    const { guestProps } = state.general;
    const { requestAccess } = state.general;
    const { reloadInitialData } = state.general;

    const { theme } = state.theme;
    let primaryColor = theme.palette.primary.main;
    let secondaryColor = theme.palette.secondary.main;
    const { setTheme } = getStoreActions().theme;

    if (state.general.basicProfileVO?.id && !reloadInitialData) {
      actions.injectScript();
      onLoaded();
      return;
    }

    try {
      const response = await getInitialData(guestProps);
      if (response?.status === 200 && response.data) {
        // New profile/init session — clear sticky gating flag before applying init payload.
        actions.setHasAccessControlledContent(false);
        actions.setBasicProfileVO(response.data.profileVO);

        const initProfileVO = response.data.profileVO;
        if (initProfileVO?.requestAccess || Boolean(initProfileVO?.additionalMap?.isProfileGated)) {
          actions.setHasAccessControlledContent(true);
        }

        // Populate Guest details
        actions.setGuestProps({
          profileId: response.data.profileVO.id,
          companyId: response.data.companyInfoVO.companyId,
          companyInfoVO: response.data.companyInfoVO,
          guestDetails: response.data.guestAccessVO || {}
        });
        // populate shared view list
        actions.setSharedViewList(response.data?.profileSharedViewVOList || []);

        // Extract landingPageViewId from init API response
        const landingPageViewId = response.data?.profileVO?.landingPageViewId || response.data?.profileSharedViewVOList?.find((view: SharedViewDetails) => view?.isLandingPage)?.id || '';

        if (landingPageViewId) {
          actions.setLandingPageViewId(landingPageViewId);
          actions.setSharedViewId(landingPageViewId);
        } else if (response.data?.profileSharedViewVOList?.length >= 1) {
          actions.setSharedViewId(response.data?.profileSharedViewVOList?.[0]?.id || '');
        }

        // With landing page, keep landing view selected (do not switch to list[0]).
        // SharedViewSelector stays hidden until verified when landingPageViewId is set.
        if (!landingPageViewId && response.data?.profileSharedViewVOList?.[0]?.id) {
          await actions.switchSharedView({ sharedViewId: response.data.profileSharedViewVOList[0].id });
        }

        // Log anonymous activity
        if (guestProps.isPublicUser && response.data.profileVO?.additionalMap?.viewerId) {
          const anonymousViewerId: string | null = setAnonymousViewerId(response.data.profileVO.additionalMap.viewerId);
          if (!(requestAccess?.id && requestAccess.userToken && (requestAccess.status === RequestedUserStatus.APPROVED || requestAccess.ndaSigned))) {
            logUserActivity(guestProps.profileId, guestProps.companyId, 'PROFILE_VIEWED', anonymousViewerId);
          }
        }

        // Update feature preferences
        actions.setFeaturePreference({
          profile_agent: response.data.additionalUIParams?.featurePref?.profile_agent || false,
          profile_agent_v2: response.data.additionalUIParams?.featurePref?.profile_agent_v2 || false
        });

        actions.setProfilesSettingsVO(response.data.profileVO?.profilesSettingsVO ?? defaultProfilesSettings);

        const tempPrimaryColor = response.data.companyInfoVO?.theme?.primaryColor;
        if (tempPrimaryColor) {
          primaryColor = changedColorsMap[tempPrimaryColor.toLowerCase()] || tempPrimaryColor;
        } else {
          primaryColor = '#7baf27';
        }

        const tempSecondary = response.data.companyInfoVO?.theme?.secondary;
        if (tempSecondary) {
          secondaryColor = changedColorsMap[tempSecondary.toLowerCase()] || tempSecondary;
        } else {
          secondaryColor = '#1c3443';
        }

        // Update Company Theme
        const palette: Palette = {
          primary: primaryColor,
          secondary: secondaryColor,
          primaryDark: String(getColorValue(primaryColor, 'dark')),
          accentColor: String(getColorValue(primaryColor, 'accent'))
        };
        setTheme({ ...palette });
        // Update Company UI Preferences
        setCompanyFormats(response.data.companyInfoVO);

        actions.injectScript();
        onLoaded();

        if (response.data.profileVO.builderType === ProfileBuilder.CUSTOM) {
          // Do not block initial profile render on custom-font metadata; LCP content can paint
          // with the base theme first, then upgrade to the custom font once that request finishes.
          getCustomFonts(response.data.companyInfoVO.companyId, response.data.profileVO.id, guestProps.isPublicUser).then(resData => {
            if (resData?.status === 200 && resData.data?.length) {
              actions.setCustomFontsVO(resData.data);
              const [fontDefinition] = resData.data;
              if (fontDefinition?.fileName) {
                setTheme({
                  ...palette,
                  typography: getCustomTypographyPalette(fontDefinition.fileName)
                });
              }
            }
          });
        }
      } else if (response?.status === 400 && response.data && ERROR_DETAILS_MAP[response.data]) {
        onLoaded(response.data as PROFILE_ERROR);
      } else {
        actions.setErrorKey(PROFILE_ERROR.INVALID_LINK);
        onLoaded(PROFILE_ERROR.INVALID_LINK);
      }
    } catch (error) {
      onLoaded(PROFILE_ERROR.SERVER_ERROR);
    }
  }),

  fetchProfileDetails: thunk(async (actions, payload, { getState }) => {
    const state = getState();
    const { guestProps } = state;

    if (!state.reloadProfile && state.profileLoaded === LoadStatus.LOADED) {
      return;
    }

    actions.setProfileLoaded(LoadStatus.LOADING);
    try {
      // Use sharedViewId if available, otherwise fall back to landingPageViewId
      const viewId = state.sharedViewId || state.landingPageViewId;
      if (guestProps.isPublicUser) {
        const response = await getPublicProfile(guestProps.profileId, guestProps.companyId, viewId || undefined);
        if (!response || response.status === 400 || !response.data) {
          actions.setErrorKey(PROFILE_ERROR.INVALID_LINK);
          return;
        }
        actions.setProfileVO(response.data.profileVO || {});
        actions.setBasicProfileVO(response.data.profileVO);
      } else {
        const response = await getProfile(viewId || undefined);
        if (!response || response.status === 400 || !response.data) {
          actions.setErrorKey(PROFILE_ERROR.INVALID_LINK);
          return;
        }
        const responseData = response.data;
        actions.setProfileVO(responseData.profileVO || {});
        actions.setBasicProfileVO(response.data.profileVO);

        // Update guestProps with ndaSignedUserVO from profileVO
        if (responseData.profileVO.additionalMap?.ndaSignedUserVO) {
          actions.updateNDASignedUserVO(responseData.profileVO.additionalMap.ndaSignedUserVO);
        }
        if (responseData.profileVO.additionalMap?.processNdaSignedUser) {
          actions.validateNdaSignedStatus({ isPublicUser: false });
        }
      }

      const response = await getProfilePreference(guestProps.isPublicUser, guestProps.companyId, WATERMARK_PREFERENCES);
      actions.setNdaContentVO(response['profileNdaVO']);
      actions.setWatermarkVO(response['profileWatermarkVO']);

      const ndaMergeTagKeyList = await getNdaMergeTags(guestProps.isPublicUser, guestProps.companyId);
      actions.setNdaMergeTagKeyList(ndaMergeTagKeyList || []);

      const { profileVO } = getState();
      if (profileVO.type === ProfileType.PUBLIC) {
        const webFormFields = await getCustomFields(guestProps.isPublicUser, guestProps.companyId, profileVO.id);
        actions.setWebFormFields(sortWebFormFields((webFormFields || []).filter(field => field.isRequired || field.isEnabled)));
      } else {
        actions.setWebFormFields([]);
      }

      actions.setReloadProfile(false);
      actions.setProfileLoaded(LoadStatus.LOADED);
    } catch (error) {
      actions.setProfileLoaded(LoadStatus.FAILED);
    }
  }),

  updateNDASignedUserVO: action((state, entry) => {
    state.guestProps.guestDetails.additonalDetails = state.guestProps.guestDetails.additonalDetails ?? {};
    state.guestProps.guestDetails.additonalDetails.ndaSignedUserVO = entry ?? {};
  }),

  updateSubscribedDetails: action((state, entry) => {
    state.guestProps.guestDetails.subscribedDetails = { ...(state.guestProps.guestDetails.subscribedDetails || {}), ...entry };
  }),

  setActiveContentId: action((state, contentActivationMap) => {
    const { contentId, isActive } = contentActivationMap;
    state.contentVOMap[contentId].isActive = isActive;
  }),

  setActiveSectionId: action((state, sectionActivationMap) => {
    const { sectionId, isActive } = sectionActivationMap;
    state.sectionVOMap[sectionId].isActive = isActive;
  }),

  setExpandedSectionId: action((state, sectionActivationMap) => {
    const { sectionId, isExpanded, isActive } = sectionActivationMap;
    state.sectionVOMap[sectionId].isExpanded = isExpanded;
    state.sectionVOMap[sectionId].isActive = isActive;
  }),

  requestAccess: {} as ProfileRequestAccessVO,
  setRequestAccess: action((state, entry) => {
    state.requestAccess = entry;
    state.guestProps.mailId = entry.mailId;
    if (state.requestAccess.id) {
      state.guestProps.isVerifiedUser = true;
    }
    if (entry.userToken) {
      state.guestProps.guestKey = entry.userToken;
    }
    if (entry) {
      sessionStorage.setItem('requestVO', JSON.stringify(entry));
    }
    // Always recalculate isAccessVerified when requestAccess is updated
    // This ensures state is updated immediately when access is granted
    const hasVerified: boolean = Boolean(entry?.id && entry.userToken && (entry.status === RequestedUserStatus.APPROVED || entry.ndaSigned));
    state.guestProps.isAccessVerified = !state.guestProps.isPublicUser || (hasVerified && state.guestProps.isOtpVerified);
    updateAccessVerifiedStatus(state.guestProps.isAccessVerified);
  }),
  setOtpVerified: action((state, entry) => {
    state.guestProps.isOtpVerified = entry;

    const hasVerified: boolean = Boolean(state.requestAccess?.id && state.requestAccess.userToken && (state.requestAccess.status === RequestedUserStatus.APPROVED || state.requestAccess.ndaSigned));

    state.guestProps.isAccessVerified = !state.guestProps.isPublicUser || (hasVerified && state.guestProps.isOtpVerified);
    updateAccessVerifiedStatus(state.guestProps.isAccessVerified);

    if (state.guestProps.isPublicUser && usageTimeTrackVO?.entityType && hasVerified) {
      usageTimeTrackVO.onHold = !state.guestProps.isAccessVerified;
      usageTimeTrackVO.anonymousViewer = null;
    }
  }),

  contentGatingMap: computed(
    [
      state => state.contentIds,
      state => state.guestProps,
      state => state.guestProps.isOtpVerified,
      state => state.contentVOMap,
      state => state.profileVO.requestAccess,
      state => state.profileVO.ndaAccess,
      state => state.requestAccess
    ],
    (...stateList) => {
      const contentGating: { [key: string]: { status: ContentGatingStatus; text: string } } = {};

      const [contentIds] = stateList;

      if (!contentIds) {
        return contentGating;
      }

      // Destructure stateList: [contentIds, guestProps, isOtpVerified, contentVOMap, profileRequestAccess, profileNdaAccess, requestAccess]
      const [, guestProps, , contentVOMap, profileRequestAccess, profileNdaAccess, requestAccess] = stateList;

      const { isPublicUser } = guestProps;

      contentIds.forEach((contentId: string) => {
        const contentVO = contentVOMap[contentId];
        const hasRequestAccess: boolean = profileRequestAccess || contentVO.requestAccess;
        const hasNDAAccess: boolean = profileNdaAccess || contentVO.ndaAccess;

        let status: ContentGatingStatus = ContentGatingStatus.UNLOCKED;
        let text: string = '';
        if ((isPublicUser && hasRequestAccess) || hasNDAAccess) {
          const needsNDASignature =
            hasNDAAccess &&
            (isPublicUser
              ? !requestAccess?.ndaSigned && (guestProps.guestDetails?.additonalDetails?.ndaSignedUserVO?.source !== 'DOCUSIGN' || guestProps.guestDetails?.additonalDetails?.ndaSignedUserVO)
              : !guestProps.guestDetails?.additonalDetails?.ndaSignedUserVO);

          if (!guestProps.isVerifiedUser) {
            status = ContentGatingStatus.LOCKED;
          } else if (requestAccess?.status === RequestedUserStatus.EXPIRED) {
            status = ContentGatingStatus.RECLAIM_ACCESS;
          } else if (requestAccess?.status === RequestedUserStatus.REQUESTED || requestAccess?.status === RequestedUserStatus.RECLAIMED) {
            status = ContentGatingStatus.APPROVAL_PENDING;
          } else if (!guestProps.isOtpVerified && requestAccess?.status === RequestedUserStatus.APPROVED) {
            status = ContentGatingStatus.VALIDATE_USING_OTP;
          } else if (needsNDASignature) {
            status = ContentGatingStatus.SIGN_NDA;
          }
          // If none of the above conditions are met, status remains UNLOCKED (default)
        } else {
          status = ContentGatingStatus.UNLOCKED;
        }
        if (status === ContentGatingStatus.LOCKED) {
          if (hasRequestAccess && hasNDAAccess) {
            text = 'REQUIRES_REQUEST_ACCESS_AND_NDA';
          } else if (hasRequestAccess) {
            text = 'REQUIRES_REQUEST_ACCESS';
          } else {
            text = 'REQUIRES_NDA';
          }
        } else if (status !== ContentGatingStatus.UNLOCKED && hasNDAAccess) {
          text = 'REQUIRES_NDA';
        }

        contentGating[contentId] = { status, text };
      });

      return contentGating;
    }
  ),

  initUsageTracker: thunk(async (actions, { contentVO }, { getStoreState }) => {
    const state = getStoreState().general;
    const { guestProps, profileVO, requestAccess } = state;
    const { companyId, isPublicUser } = guestProps;

    // Initiate usage tracker
    const cleanup = triggerUsageTracker({
      companyId,
      isPublicUser,
      profile: profileVO,
      content: contentVO,
      entityType: contentVO ? contentVO.contentType : 'PROFILE'
    });

    // Hold tracking when otp not verifed
    const hasVerified: boolean = Boolean(requestAccess?.id && requestAccess.userToken && (requestAccess.status === RequestedUserStatus.APPROVED || requestAccess.ndaSigned));
    if (isPublicUser && usageTimeTrackVO?.entityType && hasVerified) {
      usageTimeTrackVO.onHold = !guestProps.isAccessVerified;
      usageTimeTrackVO.anonymousViewer = null;
    }

    // Store the cleanup function
    actions.setUsageTrackerCleanup(cleanup);
  }),

  usageTrackerCleanup: undefined,
  setUsageTrackerCleanup: action((state, payload) => {
    state.usageTrackerCleanup = payload;
  }),

  accessibilityVO: computed(
    [
      state => state.guestProps.guestDetails?.accessType,
      state => state.guestProps.guestDetails?.accessibilityDetails,
      state => state.contentIdBySectionId,
      state => state.contentIds,
      state => state.sharedViewList,
      state => state.basicProfileVO
    ],
    (...stateList) => {
      const [accessType, accessibilityDetails, contentIdBySectionId, contentIds, sharedViewList, basicProfileVO] = stateList;

      // Construct existing accessibilityVO from guestDetails (if available)
      const existingAccessibilityVO: AccessibilityVO | undefined = accessType
        ? {
            accessType: accessType as ProfileAccessType,
            includeSummary: accessibilityDetails?.includeSummary,
            sections: accessType === ProfileAccessType.PARTIAL ? Object.keys(contentIdBySectionId) : undefined,
            contents: accessType === ProfileAccessType.PARTIAL ? contentIds : undefined
          }
        : undefined;

      // Use utility function to get the correct accessibilityVO
      const accessibilityVO = getAccessibilityVO(basicProfileVO, sharedViewList, existingAccessibilityVO, contentIdBySectionId, contentIds);

      // Add sectionContentsMap if accessType is PARTIAL (needed for store)
      if (accessibilityVO.accessType === ProfileAccessType.PARTIAL) {
        accessibilityVO.sectionContentsMap = contentIdBySectionId || {};
      }

      return accessibilityVO;
    }
  ),

  profileGatingStatus: computed(
    [state => state.guestProps, state => state.requestAccess, state => state.profileVO.requestAccess, state => state.profileVO.ndaAccess, state => state.profileVO.description],
    (...stateList) => {
      const [guestProps, requestAccess, profileRequestAccess, profileNdaAccess, profileDescription] = stateList;
      const { isPublicUser } = guestProps;

      if ((profileDescription?.length ?? 0) > 0) {
        return 'UNLOCKED';
      }

      if (isPublicUser) {
        const isRequestAccessLocked =
          requestAccess.id && (requestAccess?.status === RequestedUserStatus.REQUESTED || requestAccess?.status === RequestedUserStatus.RECLAIMED || !guestProps.isOtpVerified);
        const isNdaAccessLocked = profileNdaAccess && (!requestAccess?.ndaSigned || !guestProps.isOtpVerified);

        if (!guestProps.isVerifiedUser && (profileRequestAccess || profileNdaAccess)) {
          return 'LOCKED';
        }
        if (isRequestAccessLocked || isNdaAccessLocked) {
          return 'LOCKED';
        }
      } else if (profileNdaAccess && !guestProps.guestDetails?.additonalDetails?.ndaSignedUserVO) {
        return 'LOCKED';
      } else {
        // do nothing
      }
      return 'UNLOCKED';
    }
  ),

  overallGatingStatus: computed([state => state.contentGatingMap], contentGatingMap => {
    const statuses = Object.values(contentGatingMap).map(item => item.status);

    if (statuses.length === 0) return ContentGatingStatus.UNLOCKED;

    const statusCount = statuses.reduce(
      (acc, status) => {
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      {} as Record<ContentGatingStatus, number>
    );

    const uniqueStatuses = Object.keys(statusCount) as ContentGatingStatus[];

    if (uniqueStatuses?.length === 1) {
      return uniqueStatuses[0];
    }

    if (uniqueStatuses?.length === 2 && uniqueStatuses.includes(ContentGatingStatus.UNLOCKED)) {
      const other = uniqueStatuses.find(status => status !== ContentGatingStatus.UNLOCKED);
      return other ?? ContentGatingStatus.UNLOCKED;
    }

    return ContentGatingStatus.UNLOCKED;
  }),

  updateContentMap: action((state, entry) => {
    if (!entry.id) {
      return;
    }
    state.contentVOMap[entry.id] = { ...state.contentVOMap[entry.id], ...entry };
  }),
  isAskEnabled: computed([state => state.profilesSettingsVO], profilesSettingsVO => isProfileSettingEnabled(profilesSettingsVO, 'PROFILE_ASK')),
  isProfileAgentV2Enabled: computed([state => state.featurePreference], featurePreference => Boolean(featurePreference?.profile_agent_v2)),
  isSubscriptionEnabled: computed([state => state.profilesSettingsVO], profilesSettingsVO => isProfileSettingEnabled(profilesSettingsVO, 'PROFILE_SUBSCRIPTION')),
  hideProfileAgent: computed([state => state.isAskEnabled], isAskEnabled => !isAskEnabled),
  hideSubscription: computed([state => state.isSubscriptionEnabled, state => state.guestProps, state => state.requestAccess], (isSubscriptionEnabled, guestProps, requestAccess) => {
    if (isGuestAlreadySubscribed(guestProps, requestAccess)) {
      return false;
    }
    return !isSubscriptionEnabled;
  }),
  /** Used by Subscribe header CTA variant hierarchy (Ask > Get Access > Subscribe). Header Get Access is for no-landing-page TCs only. */
  showGetAccessButton: computed(
    [
      state => state.profileVO.builderType,
      state => state.profileVO.type,
      state => Boolean(state.profileVO.requestAccess),
      state => Boolean(state.profileVO.ndaAccess),
      state => state.contentIds.length > 0,
      state => Object.values(state.contentVOMap).some(item => item.requestAccess),
      state => Object.values(state.contentVOMap).some(item => item.ndaAccess),
      state => state.overallGatingStatus,
      state => state.guestProps.isAccessVerified,
      state => state.basicProfileVO.landingPageViewId
    ],
    (builderType, profileType, profileRequestAccess, profileNdaAccess, hasContents, contentHasRequestAccess, contentHasNdaAccess, overallGatingStatus, isAccessVerified, landingPageViewId) => {
      // Landing-page TCs use the body banner for Verify/Get Access; header CTA stays off.
      if (landingPageViewId || profileType !== ProfileType.PUBLIC || isAccessVerified) {
        return false;
      }
      if (!hasContents || overallGatingStatus === ContentGatingStatus.UNLOCKED) {
        return false;
      }
      const isCustomBuilder = builderType === ProfileBuilder.CUSTOM;
      const currentViewHasRequestAccessGating = profileRequestAccess || contentHasRequestAccess;
      const showGetAccessForCustom = currentViewHasRequestAccessGating;
      const showGetAccessForStandard = contentHasRequestAccess || contentHasNdaAccess || profileRequestAccess || profileNdaAccess;
      return isCustomBuilder ? showGetAccessForCustom : showGetAccessForStandard;
    }
  ),
  reclaimProfileAccessRequest: thunk(async (actions, { payload, onSuccess }) => {
    await reclaimProfileAccessRequest(payload, (requestAccess: ProfileRequestAccessVO) => {
      const timeout = setTimeout(() => {
        actions.setRequestAccess(requestAccess);
        onSuccess(requestAccess);
        clearTimeout(timeout);
      }, 1000);
    });
  }),

  retryDownload: thunk((actions, payLoad) => {
    const { downloadProfileContent } = actions;
    downloadProfileContent({ ...payLoad });
  }),

  downloadProfileContent: thunk((actions, payLoad, { getState }) => {
    const { setErrorKey, retryDownload } = actions;
    const { docIds, questionnaireIds, attachmentIds = [], contentVO, setDownloadPending, isBulkDownload, viewSourceCallBack } = payLoad;
    const { guestProps, profileVO, contentVOMap, contentGatingMap } = getState();
    const { isPublicUser, guestKey, isAccessVerified, companyId } = guestProps;
    if (setDownloadPending) {
      setDownloadPending(true);
    }

    let postBody = {
      docIds,
      questionnaireIds,
      attachmentIds
    };

    const contentIdList: string[] = [];
    // in case of bulk download , get all the contents and filter them based on lock status
    if (isBulkDownload) {
      postBody = {
        docIds: [],
        questionnaireIds: [],
        attachmentIds: []
      };
      Object.values(contentVOMap)?.forEach(content => {
        const isNotGated = contentGatingMap[content?.id]?.status === ContentGatingStatus.UNLOCKED && !content?.viewOnlyAccess;
        const contentType = content?.contentType;
        const id = content?.contentInfo?.id;
        const contentId = content?.id;

        if (contentType !== ProfileContentType.CONTROLS && contentType !== ProfileContentType.IMAGE) {
          if (isNotGated) {
            if (contentType === ProfileContentType.DOCUMENT) {
              postBody?.docIds?.push(id);
            } else {
              postBody?.questionnaireIds?.push(id);
            }
            contentIdList.push(contentId);
          }
        }
      });
    }

    const isPublic: boolean = isPublicUser && !isAccessVerified;
    downloadContents(profileVO.companyId, profileVO.id, isPublic, postBody, (response: DownloadContentsCallbackResponse | null | undefined) => {
      if (response) {
        const { batchId } = response;
        if (!batchId) {
          return;
        }
        const fileVO = cloneDeep(contentVO?.contentInfo) || {};

        // callback to get batch details
        downloadBatchInitiator(profileVO.companyId, profileVO.id, batchId, isPublic, (batchVO: unknown) => {
          // this.callback ll execute only if the batch status is 'COMPLETED'
          const batch = batchVO as DownloadBatchDetailsCallbackVO | null | undefined;
          const resultMap = batch?.data?.response;
          if (!resultMap) {
            return;
          }

          // updating file name with extension
          const contentName = resultMap?.docDetails?.contentName || contentVO.name;
          fileVO.name = contentName;
          if (resultMap?.docDetails?.type === 'PROJECT_DOCUMENT') {
            fileVO.fileName = contentName;
          }

          // update the file VO with updated file details
          const docdetails = resultMap?.docDetails;
          fileVO.fileId = docdetails?.streamId;
          fileVO.downloadAuthKey = batch?.data?.downloadAuthKey;
          const downloadUrl = getFileDownloadURL(fileVO as FileDownloadInput, {
            urlPrefix: isAccessVerified ? 'profiles' : `public/${companyId}/profiles`,
            guestKey: isAccessVerified ? guestKey : undefined
          });

          if (downloadUrl) {
            const isAnonymousViewer: boolean = Boolean(isPublicUser && !isAccessVerified);
            let anonymousViewId = null;
            if (isAnonymousViewer) {
              anonymousViewId = getAnonymousViewerId();
            }
            if (viewSourceCallBack) {
              const downloadSrcVO = cloneDeep(contentVO?.contentInfo || contentVO);
              if (downloadSrcVO) {
                downloadSrcVO.name = contentName;
                downloadSrcVO.downloadAuthKey = batch?.data?.downloadAuthKey;
                if (docdetails?.isWatermarked) {
                  downloadSrcVO.fileType = 'application/pdf';
                } else {
                  // do nothing
                }
                viewSourceCallBack(downloadSrcVO as WatermarkSourceUpdate);
              }
            } else {
              handleProfileDownload(
                downloadUrl,
                anonymousViewId,
                e => {
                  setDownloadPending(false);
                  setErrorKey(e);
                },
                () => {
                  // Track analytics ONLY after successful download completion
                  if (isBulkDownload && contentIdList && contentIdList.length > 1) {
                    trackAnalyticsForBulkDownload(profileVO.id, contentIdList, isPublic, guestProps);
                  }
                }
              );
            }
            if (setDownloadPending) {
              setDownloadPending(false);
            }
          }

          // process for reupload content if the file is corrupted.
          if (contentVO?.contentType === ProfileContentType.PD && contentVO?.sourceStatus === ContentSourceStatus.ERROR) {
            if (contentVO.retryCount && contentVO.retryCount > 0) {
              setDownloadPending(true);
              if (!contentVO.retryCount || contentVO.retryCount <= 0) {
                reuploadContent(
                  contentVO.profileId,
                  contentVO.sectionId,
                  contentVO.id,
                  (updatedContent: ProfileContentVO) => {
                    // handle download again
                    retryDownload({ ...payLoad, contentVO: updatedContent });
                  },
                  () => {
                    setDownloadPending(false);
                  },
                  guestProps
                );
              }
            }
          } else if (setDownloadPending) {
            setDownloadPending(false);
          }
        });
      }
    });
  }),

  contentRedirectionURL: thunk((_, { contentId }, { getState }) => {
    const { contentVOMap, guestProps } = getState();
    const contentVO = contentVOMap[contentId];
    return getContentLink(contentVO, contentVO.contentType, guestProps);
  }),

  globalUnlockRenderer: {},
  setGlobalUnlockRederer: action((state, payload) => {
    state.globalUnlockRenderer = payload;
  }),

  validateNdaSignedStatus: thunk(async (actions, { isPublicUser, mailId }, { getState }) => {
    const { setValidateNdaSignStatus, updateNDASignedUserVO } = actions;
    const { guestProps } = getState();
    const { companyId, profileId } = guestProps;

    setValidateNdaSignStatus(true);
    const response = await getNdaSignedStatus(isPublicUser, companyId, profileId, mailId);
    if (response && response.status === 200 && response.data) {
      const docuSignDetailsFromServerCallBack = {
        docusignUserAvailable: response.data?.docusignUserAvailable || false,
        docusignRecipientSignedMap: response.data?.docusignRecipientSignedMap || {},
        userNameAvailable: response.data?.userNameAvailable || false,
        signingUrlForDocusign: response.data?.signingUrlForDocusign || ''
      };
      guestProps.guestDetails.docuSignDetailsFromServerCallBack = docuSignDetailsFromServerCallBack;

      if (response.data.ndaSignedUserVO) {
        updateNDASignedUserVO(response.data.ndaSignedUserVO);
      }
    }
    setTimeout(() => setValidateNdaSignStatus(false), 1000);
  }),

  updateDocusignDetails: action((state, entry) => {
    state.guestProps.guestDetails.docuSignDetailsFromServerCallBack = entry ?? {};
  }),

  updateDocusignSigningURL: action((state, entry) => {
    if (state.guestProps.guestDetails.docuSignDetailsFromServerCallBack) {
      state.guestProps.guestDetails.docuSignDetailsFromServerCallBack.signingUrlForDocusign = entry ?? '';
    }
  }),

  customFontsVO: [],
  setCustomFontsVO: action((state, payload) => {
    state.customFontsVO = payload;
  }),

  needNdaIconRefresh: false,
  setNeedNdaIconRefresh: action((state, payload) => {
    state.needNdaIconRefresh = payload;
  }),

  fetchSessions: thunk(async (actions, _, { getStoreState }) => {
    const state = getStoreState() as GlobalStore;
    const { guestProps } = state.general;
    try {
      const response = await getSessions(guestProps);

      if (response?.status === 200 && response.data) {
        const sessions = Array.isArray(response.data) ? response.data : response.data.sessions;

        if (sessions && sessions.length > 0) {
          actions.setSessionDetails(sessions);
        } else {
          actions.setSessionDetails([]);
        }
      } else {
        actions.setSessionDetails([]);
      }
    } catch (e) {
      actions.setSessionDetails([]);
    }
  }),

  initFAQDatasetup: thunk(async (actions, payload, { getStoreState }) => {
    const { companyId, profileId, isPublicUser, editor, details, getFAQs: fetchFAQsFromPayload, getSubProcessors: fetchSubProcessorsFromPayload, sharedViewId } = payload;

    const setupDetails = details as
      | {
          sectionId?: string;
          isLoadMore?: boolean;
          designFaqSections?: import('@utils/faq/FAQSectionUtils').DesignFaqSectionEntry[] | null;
          successCallback?: (data: ProfileFAQVO[]) => void;
        }
      | undefined;
    const isLoadMore = Boolean(setupDetails?.isLoadMore);
    const loadMoreSectionId = isLoadMore && setupDetails?.sectionId ? setupDetails.sectionId : undefined;

    const updateFAQDetails = editor?.config?.storeManager?.updateFAQDetails;
    const faqResponseVO = editor?.config?.storeManager?.faqResponseVO;
    const updateSubprocessorCategory: (details: UpdateSubprocessorCategoryPayload) => void = editor?.config?.storeManager?.updateSubprocessorCategory;

    if (updateFAQDetails || updateSubprocessorCategory) {
      const state = getStoreState().general;
      const designFaqSections = setupDetails?.designFaqSections ?? state.customBuilderFaqSections;

      const shouldCallWithoutViewId =
        state.guestProps.isAccessVerified && !state.hasPreferenceData && isPublicUser && state.basicProfileVO.builderType === ProfileBuilder.CUSTOM && state.basicProfileVO.type === ProfileType.PUBLIC;

      // Treat empty string as undefined - if sharedViewId is empty, don't pass viewId
      const normalizeViewId = (id?: string): string | undefined => id?.trim() || undefined;
      const viewId = shouldCallWithoutViewId ? undefined : (normalizeViewId(sharedViewId) ?? normalizeViewId(state.landingPageViewId));
      const trackedViewId = viewId ?? '';

      const hasSharedViewIdChanged = state.previousFAQSharedViewId !== trackedViewId;

      if (hasSharedViewIdChanged) {
        actions.setPreviousFAQSharedViewId(trackedViewId);
        lastGuestFaqFetchKey = '';
        if (!isLoadMore && editor?.config?.storeManager?.faqVoMap) {
          editor.config.storeManager.faqVoMap = {};
        }
      }
      const isCustomBuilder = state.basicProfileVO?.builderType === ProfileBuilder.CUSTOM;

      const faqSectionIds = resolveFaqFetchSectionIds(
        isCustomBuilder,
        {
          isLoadMore,
          sectionId: loadMoreSectionId ?? setupDetails?.sectionId,
          knownSectionIds: Object.keys(faqResponseVO?.sectionFaqs || {})
        },
        state.basicProfileVO?.sections || state.profileVO?.sections,
        'FAQ',
        designFaqSections
      );
      const nextCursor = isLoadMore ? getFaqLoadMoreCursor(faqResponseVO, faqSectionIds, true) : undefined;

      const sortedSectionIds = [...(faqSectionIds || [])].sort().join(',');
      const fetchKey = `${profileId}|${trackedViewId}|${sortedSectionIds}|${nextCursor ?? ''}|${isLoadMore ? '1' : '0'}`;
      if (!isLoadMore && fetchKey === lastGuestFaqFetchKey) {
        return;
      }
      lastGuestFaqFetchKey = fetchKey;

      fetchFAQsFromPayload(
        companyId,
        profileId,
        isPublicUser,
        nextCursor,
        viewId,
        (result: ProfileFAQResponse) => {
          const mergedResult = isLoadMore && faqResponseVO ? mergeGuestFAQPage(faqResponseVO, result) : result;
          let artifacts = [...(mergedResult.artifacts || [])];
          const existingSubArtifacts = (faqResponseVO?.artifacts || [])?.filter((a: ProfileFAQVO) => a?.type === ProfileContentType.SUB_PROCESSOR);
          const shouldFetchSubprocessors = !isLoadMore && (!nextCursor || existingSubArtifacts?.length === 0);

          if (!shouldFetchSubprocessors) {
            artifacts = [...artifacts, ...existingSubArtifacts];
            mergedResult.allSubprocessorCategories = faqResponseVO?.allSubprocessorCategories;
            mergedResult.artifacts = artifacts;
            handleFAQSuccess(mergedResult, editor, setupDetails, isLoadMore, result);
          } else {
            fetchSubProcessorsFromPayload(
              companyId,
              profileId,
              isPublicUser,
              (subprocessorResp: ProfileFAQResponse) => {
                const subArtifacts = (subprocessorResp?.artifacts || [])?.map(resp => ({ ...resp, type: ProfileContentType.SUB_PROCESSOR }));
                const mergedSubArtifacts = subArtifacts?.length > 0 ? subArtifacts : existingSubArtifacts;
                artifacts = [...artifacts, ...mergedSubArtifacts];
                mergedResult.allSubprocessorCategories = subprocessorResp?.allSubprocessorCategories || faqResponseVO?.allSubprocessorCategories;
                mergedResult.artifacts = artifacts;
                handleFAQSuccess(mergedResult, editor, setupDetails, isLoadMore, result);
              },
              viewId
            );
          }
        },
        faqSectionIds
      );
    }
  }),

  switchSharedView: thunk(async (actions, payload, { getState }) => {
    const { sharedViewId } = payload;
    const { setProfileLoaded, setSharedViewId } = actions;
    const state = getState();
    const { guestProps } = state;
    setProfileLoaded(LoadStatus.LOADING);
    setSharedViewId(sharedViewId);

    let response;
    if (guestProps.isPublicUser) {
      response = await getPublicProfile(guestProps.profileId, guestProps.companyId, sharedViewId || undefined);
    } else {
      response = await getProfile(sharedViewId || undefined);
    }

    if (!response || response.status === 400 || !response.data) {
      actions.setErrorKey(PROFILE_ERROR.INVALID_LINK);
      return;
    }
    setProfileLoaded(LoadStatus.LOADED);
    const responseData = response.data;
    actions.setProfileVO(responseData.profileVO || {});
    actions.setBasicProfileVO(response.data.profileVO);
  }),

  hasPreferenceData: false,
  setHasPreferenceData: action((state, payload) => {
    state.hasPreferenceData = payload;
  }),

  hasLoadedPreferencesAfterVerification: false,
  setHasLoadedPreferencesAfterVerification: action((state, payload) => {
    state.hasLoadedPreferencesAfterVerification = payload;
  }),

  hasAccessControlledContent: false,
  setHasAccessControlledContent: action((state, payload) => {
    state.hasAccessControlledContent = payload;
  }),

  loadSharedViewIdsFromPreferences: thunk(async (actions, _, { getState }) => {
    const state = getState();
    const { hasPreferenceData, guestProps, hasLoadedPreferencesAfterVerification, requestAccess, landingPageViewId } = state;

    // Guard: Prevent redundant init call if already loaded after verification
    if (!hasPreferenceData || !guestProps.isAccessVerified || hasLoadedPreferencesAfterVerification) {
      return;
    }

    // If landingPageViewId already exists from initial init API call, check if it's in the user's sharedViewIds
    if (landingPageViewId) {
      const preferenceSharedViewIds = requestAccess?.preferences?.accessibility_details?.sharedViewIds;
      const isLandingPageInSharedViews = preferenceSharedViewIds?.includes(landingPageViewId);

      try {
        const response = await getInitialData(guestProps);
        if (response?.status === 200) {
          const profileSharedViewVOList = response?.data?.profileSharedViewVOList;
          if (profileSharedViewVOList && profileSharedViewVOList.length > 0) {
            actions.setSharedViewList(profileSharedViewVOList);
          }

          if (!isLandingPageInSharedViews && preferenceSharedViewIds?.length) {
            // landingPageViewId not in user's shared views — remove it and use 0th index
            actions.setLandingPageViewId('');
            const fallbackViewId = preferenceSharedViewIds[0];
            actions.setSharedViewId(fallbackViewId);
            await actions.switchSharedView({ sharedViewId: fallbackViewId });
          } else if (!profileSharedViewVOList?.length) {
            const { companyId, profileId, isPublicUser } = guestProps;
            const isCustomBuilder = state.basicProfileVO?.builderType === ProfileBuilder.CUSTOM;
            const faqSectionIds = getCustomBuilderFaqSectionIds(isCustomBuilder, state.basicProfileVO?.sections, 'FAQ', state.customBuilderFaqSections);
            getFAQs(
              companyId,
              profileId,
              isPublicUser,
              undefined,
              undefined,
              () => {
                getSubProcessors(
                  companyId,
                  profileId,
                  isPublicUser,
                  () => {
                    actions.setSharedViewId('');
                    actions.setLandingPageViewId('');
                    actions.setReloadProfile(true);
                    actions.fetchProfileDetails();
                  },
                  undefined
                );
              },
              faqSectionIds
            );
          }

          actions.setHasLoadedPreferencesAfterVerification(true);
        }
      } catch (error) {
        // Error handling is done in getInitialData service
      }
      return;
    }

    // Check if sharedViewId exists in preferences (from requestAccess)
    const preferenceSharedViewIds = requestAccess?.preferences?.accessibility_details?.sharedViewIds;
    const preferenceSharedViewId = preferenceSharedViewIds?.[0] ?? null;
    try {
      // Always call INIT API first
      const response = await getInitialData(guestProps);

      if (response?.status === 200) {
        const profileSharedViewVOList = response?.data?.profileSharedViewVOList;

        // Set shared view list if available
        if (profileSharedViewVOList) {
          actions.setSharedViewList(profileSharedViewVOList);
        }

        // Check if profileSharedViewVOList has data
        const hasViewData = !!profileSharedViewVOList?.length;

        if (hasViewData) {
          const extractedLandingPageViewId = response.data?.profileVO?.landingPageViewId || profileSharedViewVOList?.find((view: SharedViewDetails) => view?.isLandingPage)?.id || '';

          // Only use landingPageViewId if it's present in the user's sharedViewIds
          const isLandingPageInPreferences = extractedLandingPageViewId && preferenceSharedViewIds?.includes(extractedLandingPageViewId);
          const effectiveLandingPageViewId = isLandingPageInPreferences ? extractedLandingPageViewId : '';

          // Priority: preference sharedViewId > landingPageViewId (if in sharedViewIds) > 0th index from INIT API
          const viewIdToUse = preferenceSharedViewId || effectiveLandingPageViewId || profileSharedViewVOList[0]?.id || '';

          if (viewIdToUse) {
            if (preferenceSharedViewId) {
              actions.setSharedViewId(preferenceSharedViewId);
            } else {
              if (effectiveLandingPageViewId) {
                actions.setLandingPageViewId(effectiveLandingPageViewId);
              }
              actions.setSharedViewId(viewIdToUse);
            }
            await actions.switchSharedView({ sharedViewId: viewIdToUse });
          }
        } else {
          // No view data - call GET API without viewId
          actions.setSharedViewId('');
          actions.setLandingPageViewId('');
          actions.setReloadProfile(true);
          actions.fetchProfileDetails();
        }

        // Mark preferences as loaded after verification to prevent duplicate calls
        actions.setHasLoadedPreferencesAfterVerification(true);
      }
    } catch (error) {
      // Error handling is done in getInitialData service
    }
  }),

  handleProfileLoadLogic: thunk(async (actions, { hasCalledReload }, { getState }) => {
    const state = getState();
    const { guestProps, basicProfileVO, requestAccess, hasPreferenceData, landingPageViewId, sharedViewList } = state;
    const { isAccessVerified, isPublicUser } = guestProps;
    const { builderType, type: profileType } = basicProfileVO;

    const shouldLoadProfile = isAccessVerified && !hasPreferenceData && !hasCalledReload && isPublicUser && isCustomPublicProfile(builderType, profileType) && !hasSharedViewIds(requestAccess);

    // A Landing Page Quick Link opens inside that view; the full-profile reload waits until linkToken is consumed.
    if (!shouldLoadProfile || isLandingPageQuickLink(landingPageViewId)) {
      return false;
    }
    const isApproved = requestAccess?.status === RequestedUserStatus.APPROVED;
    const isPartialAccess = hasSharedViewIds(requestAccess);

    // Full access after OTP: reload profile without viewId (do not switch to any shared view).
    if (isApproved && !isPartialAccess) {
      actions.setProfileLoaded(LoadStatus.LOADING);
      actions.setSharedViewId('');
      actions.setLandingPageViewId('');
      actions.setReloadProfile(true);
      return true;
    }

    // Landing page: clear and reload full profile after access (do this before list[0] switch)
    if (landingPageViewId) {
      actions.setProfileLoaded(LoadStatus.LOADING);
      actions.setSharedViewId('');
      actions.setLandingPageViewId('');
      actions.setReloadProfile(true);
      return true;
    }

    // Check if profileSharedViewVOList exists in INIT API response and has data
    if (sharedViewList && sharedViewList?.length > 0 && sharedViewList?.[0]?.id) {
      await actions.switchSharedView({ sharedViewId: sharedViewList?.[0]?.id || '' });
      return true;
    }

    actions.setProfileLoaded(LoadStatus.LOADED);
    actions.setReloadProfile(false);
    return true;
  }),

  // Effect that watches profileId and isAccessVerified to update hasPreferenceData
  onProfileAccessChanged: effectOn(
    [state => state.guestProps.profileId, state => state.guestProps.isAccessVerified, state => state.requestAccess?.preferences?.accessibility_details?.sharedViewIds],
    (actions, change, { getState }) => {
      const state = getState();
      const { guestProps, basicProfileVO, requestAccess, sharedViewId, landingPageViewId } = state;
      const { profileId, isAccessVerified } = guestProps;
      const { builderType, type: profileType } = basicProfileVO;

      // Reset tracking flag when access is lost
      if (!isAccessVerified) {
        actions.setHasLoadedPreferencesAfterVerification(false);
      }

      // Reset tracking flag when access is lost
      if (!isAccessVerified) {
        actions.setHasLoadedPreferencesAfterVerification(false);
      }

      // Early return if profileId doesn't exist or conditions not met
      if (!profileId || !isAccessVerified || !isCustomPublicProfile(builderType, profileType)) {
        return;
      }

      const isApproved = requestAccess?.status === RequestedUserStatus.APPROVED;
      const isPartialAccess = hasSharedViewIds(requestAccess);

      // Full access: remove shared view info and trigger existing reload flow (GET/FAQ without viewId).
      if (guestProps.isPublicUser && isApproved && !isPartialAccess) {
        if (isLandingPageQuickLink(landingPageViewId)) {
          return;
        }
        actions.setSharedViewList([]);
        if (sharedViewId || landingPageViewId) {
          actions.setSharedViewId('');
          actions.setLandingPageViewId('');
        }
        actions.setReloadProfile(true);
        return;
      }

      const preferenceDataValue = hasPreferenceDataValue(requestAccess);
      actions.setHasPreferenceData(preferenceDataValue);

      // Load shared view IDs when preferences exist and access is verified
      if (preferenceDataValue && isAccessVerified) {
        actions.loadSharedViewIdsFromPreferences();
      }
    }
  )
} as IGeneralStore;

function handleFAQSuccess(
  result: ProfileFAQResponse,
  editor: Editor | null | undefined,
  details?: { successCallback?: (data: ProfileFAQVO[]) => void; sectionId?: string },
  isLoadMore?: boolean,
  incomingPage?: ProfileFAQResponse
) {
  const faqEditor = editor as FAQDesignerEditor | null | undefined;
  const updateFAQDetails = faqEditor?.config?.storeManager?.updateFAQDetails;
  const faqVoMap = faqEditor?.config?.storeManager?.faqVoMap;
  const setFAQResponseVO = faqEditor?.config?.storeManager?.setFAQResponseVO;
  const updateSubprocessorCategory: ((d: UpdateSubprocessorCategoryPayload) => void) | undefined = faqEditor?.config?.storeManager?.updateSubprocessorCategory;

  if (result && (result?.artifacts || result?.allSubprocessorCategories)) {
    if (result?.artifacts) {
      setFAQResponseVO?.({ faqResponseVO: result });
      const updatedMap = result?.artifacts?.reduce(
        (map, item) => {
          map[item.id] = item;
          return map;
        },
        {} as { [key: string]: ProfileFAQVO }
      );

      const data = isLoadMore ? { ...faqVoMap, ...updatedMap } : { ...updatedMap };

      updateFAQDetails?.({
        faqVoMap: data
      });

      if (isLoadMore && details?.successCallback) {
        details.successCallback(getFaqsForSectionLoadMore(incomingPage ?? result, details.sectionId));
      } else {
        details?.successCallback?.(result?.artifacts);
      }
    }

    if (result?.allSubprocessorCategories) {
      updateSubprocessorCategory?.({
        subprocessorCategories: result.allSubprocessorCategories
      });
    }

    faqEditor?.runCommand('refresh-faq');
  }
}

export default generalStore;
